import { ModalGeneric } from "@/components/utils/ModalGeneric";
import ConfigDesks from "@/services/configDesks";
import ChatService from "@/services/ChatService";
import { useQuery } from "@tanstack/react-query";
import { Search, Users, MessageCircle, Plus, X, Upload } from "lucide-react";
import { useState, useRef, useMemo, useEffect } from "react";
import ImageGeneric from "@/assets/images/lg.png";
import { toast } from "react-toastify";
import { useLayout } from "@/contexts/UseLayout";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";

interface User {
  id: number;
  name: string;
  last_name: string;
  email: string;
  filePath?: string;
  empresa?: {
    nome_fantasia: string;
  };
}

interface Props {
  idMenu: any;
  setNewChat: (value: boolean) => void;
  handleOpenChat: (room: any) => void;
}

export function NewChatModalImproved({
  idMenu,
  setNewChat,
  handleOpenChat,
}: Props) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [chatType, setChatType] = useState<"individual" | "group">(
    "individual",
  );
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [groupName, setGroupName] = useState("");
  const [groupImage, setGroupImage] = useState<File | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const itemsPerPage = 8;
  const { getChats } = useLayout();

  // Criar função debounced para atualizar o termo de busca
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchTerm(value);
        setCurrentPage(1); // Reset page when search changes
      }, 500),
    [],
  );

  const {
    data: contatos,
    isPending,
    isFetching,
  } = useQuery({
    queryKey: ["users_chat", debouncedSearchTerm, currentPage],
    queryFn: async () =>
      await ConfigDesks.getContatosBusca(
        currentPage,
        itemsPerPage,
        debouncedSearchTerm,
      ),
    enabled: idMenu === "mensagem",
    retry: 2,
    staleTime: 1000 * 60 * 5,
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // Cleanup do debounce quando o componente for desmontado
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleUserSelect = (user: User) => {
    if (chatType === "individual") {
      handleCreateIndividualChat(user);
    } else {
      if (selectedUsers.find((u) => u.id === user.id)) {
        setSelectedUsers((prev) => prev.filter((u) => u.id !== user.id));
      } else {
        setSelectedUsers((prev) => [...prev, user]);
      }
    }
  };

  const handleCreateIndividualChat = async (user: User) => {
    setIsCreating(true);
    try {
      const formData = new FormData();
      formData.append("name", `${user.name} ${user.last_name}`);
      formData.append("guests", user.id.toString());
      formData.append("is_group", "false");

      const response = await ChatService.criarSala(formData);

      if (response?.data?.room) {
        handleOpenChat(response.data.room);
        setNewChat(false);
        toast.success(t("chatModule.messages.conversation_created"));
        getChats();
      } else {
        toast.error(t("chatModule.errors.create_conversation"));
      }
    } catch (error) {
      console.error("Erro ao criar conversa:", error);
      toast.error(t("chatModule.errors.create_conversation"));
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateGroup = async () => {
    if (selectedUsers.length < 2) {
      toast.warning(t("chatModule.warnings.select_min_users"));
      return;
    }

    if (!groupName.trim()) {
      toast.warning(t("chatModule.warnings.group_name_required"));
      return;
    }

    setIsCreating(true);
    try {
      const formData = new FormData();
      formData.append("name", groupName);
      formData.append("guests", selectedUsers.map((u) => u.id).join(", "));
      formData.append("is_group", "true");

      if (groupImage) {
        formData.append("group_image", groupImage);
      }

      const response = await ChatService.criarSala(formData);

      if (response?.data?.room) {
        handleOpenChat(response.data.room);
        setNewChat(false);
        toast.success(t("chatModule.messages.ad_room_created"));
        getChats();
      } else {
        toast.error(t("chatModule.errors.create_ad_room"));
      }
    } catch (error) {
      console.error("Erro ao criar Ad Room:", error);
      toast.error(t("chatModule.errors.create_ad_room"));
    } finally {
      setIsCreating(false);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setGroupImage(file);
    }
  };

  const removeSelectedUser = (userId: number) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const handleNextPage = () => {
    if (contatos?.data?.qtdRegistros > currentPage * itemsPerPage) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return (
    <ModalGeneric
      onClose={() => setNewChat(false)}
      size="max-w-2xl w-full max-h-[90%] overflow-y-auto"
    >
      <div className="space-y-2">
        <div className="grid grid-cols-2 items-start">
          <div>
            <h3 className="text-lg font-semibold">
              {t("chatModule.modal.new_conversation")}
            </h3>
            <p className="text-sm text-gray-500">
              {t("chatModule.modal.select_type_and_contacts")}
            </p>
          </div>

          <div className="flex gap-2 ml-auto">
            <button
              onClick={() => {
                setChatType("individual");
                setSelectedUsers([]);
                setGroupName("");
                setGroupImage(null);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                chatType === "individual"
                  ? "bg-azul-400 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <MessageCircle size={16} />
              {t("chatModule.modal.individual")}
            </button>
            <button
              onClick={() => {
                setChatType("group");
                setSelectedUsers([]);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                chatType === "group"
                  ? "bg-azul-400 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <Users size={16} />
              {t("chatModule.modal.ad_rooms")}
            </button>
          </div>
        </div>

        {chatType === "group" && (
          <div className="grid grid-cols-3 gap-3 bg-gray-50 rounded-lg">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("chatModule.modal.group_name")}*
              </label>
              <input
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder={t("chatModule.modal.group_name_placeholder")}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-azul-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("chatModule.modal.group_image")}
              </label>
              <div className="flex flex-col">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
                >
                  <Upload size={16} />
                  {groupImage
                    ? t("chatModule.modal.change_image")
                    : t("chatModule.modal.select_image")}
                </button>
                {groupImage && (
                  <span className="text-sm text-gray-600 truncate">
                    {groupImage.name}
                  </span>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
            </div>
          </div>
        )}

        {chatType === "group" && selectedUsers.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700">
              {t("chatModule.modal.selected_users", {
                count: selectedUsers.length,
              })}
            </h4>
            <div className="flex flex-wrap gap-2">
              {selectedUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center gap-1 bg-azul-400/20 text-azul-700 px-2 py-1 rounded-md text-xs"
                >
                  <span>
                    {user.name} {user.last_name}
                  </span>
                  <button
                    onClick={() => removeSelectedUser(user.id)}
                    className="hover:bg-azul-200 rounded-full p-0.5"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {chatType === "individual"
              ? t("chatModule.modal.select_contact")
              : t("chatModule.modal.add_members")}
          </label>
          <div className="flex">
            <input
              type="text"
              placeholder={t("chatModule.search.placeholder")}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md text-sm focus:outline-none focus:ring-2 focus:ring-azul-400"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <button className="bg-azul-400 text-white px-3 py-2 rounded-r-md hover:bg-azul-500 flex items-center justify-center">
              {isFetching ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Search size={16} />
              )}
            </button>
          </div>
        </div>

        <div className="space-y-2 max-h-64 overflow-y-auto">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">
              {t("chatModule.modal.contacts_found", {
                count: contatos?.data?.qtdRegistros || 0,
              })}
            </span>
          </div>

          {isPending ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-gray-100 rounded-lg"
                >
                  <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
                  <div className="flex-1">
                    <div className="w-32 h-4 bg-gray-300 rounded mb-2"></div>
                    <div className="w-24 h-3 bg-gray-300 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {contatos?.data?.data?.map((user: User) => {
                const isSelected = selectedUsers.find((u) => u.id === user.id);

                return (
                  <button
                    key={user.id}
                    onClick={() => handleUserSelect(user)}
                    disabled={isCreating}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                      chatType === "group" && isSelected
                        ? "bg-azul-400/20 border-2 border-azul-400"
                        : "bg-gray-100 hover:bg-gray-200"
                    } ${isCreating ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <img
                      src={user.filePath ?? ImageGeneric}
                      alt={`${user.name} ${user.last_name}`}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-sm text-gray-900">
                        {user.name} {user.last_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {user.empresa?.nome_fantasia ?? user.email}
                      </p>
                    </div>
                    {chatType === "group" && isSelected && (
                      <div className="w-5 h-5 bg-azul-400 text-white rounded-full flex items-center justify-center">
                        <Plus size={12} className="rotate-45" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-gray-200 rounded text-sm disabled:opacity-50"
          >
            {t("common:pagination.previous")}
          </button>
          <span className="text-sm text-gray-600">
            {t("common:pagination.page")} {currentPage}
          </span>
          <button
            onClick={handleNextPage}
            disabled={
              contatos?.data?.qtdRegistros <= currentPage * itemsPerPage
            }
            className="px-3 py-1 bg-gray-200 rounded text-sm disabled:opacity-50"
          >
            {t("common:pagination.next")}
          </button>
        </div>

        {chatType === "group" && (
          <button
            onClick={handleCreateGroup}
            disabled={
              selectedUsers.length < 2 || !groupName.trim() || isCreating
            }
            className="w-full py-3 bg-azul-400 text-white rounded-lg font-medium hover:bg-azul-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating
              ? t("chatModule.modal.creating")
              : t("chatModule.modal.create_ad_room", {
                  count: selectedUsers.length,
                })}
          </button>
        )}
      </div>
    </ModalGeneric>
  );
}
